import {Component, inject, ViewChild, ViewEncapsulation} from '@angular/core';
import {Button} from "primeng/button";
import {DropdownModule} from "primeng/dropdown";
import {FormsModule} from "@angular/forms";
import {JsonPipe, NgClass, NgForOf, NgIf, NgOptimizedImage} from "@angular/common";
import {GroupsService} from "../../data/services/groups.service";
import {Group, ImageDetail, ImageEditorImageType, Item, SJC} from "../../data/types/groups/types";
import {MakeService} from "../../data/services/make.service";
import {Make} from "../../data/types/makes/types";
import {TableLazyLoadEvent, TableModule} from "primeng/table";
import {CardModule} from "primeng/card";
import {CheckboxModule} from "primeng/checkbox";
import {DividerModule} from "primeng/divider";
import {ChipsModule} from "primeng/chips";
import {IconFieldModule} from "primeng/iconfield";
import {InputIconModule} from "primeng/inputicon";
import {InputSwitchModule} from "primeng/inputswitch";
import {DialogModule} from "primeng/dialog";
import {UpdateImageCaption, UpdateImageGroup, UpdateImageSafetyAndCostCaptions} from "../../data/types/groups/params";
import {ImageService} from "../../data/services/image.service";
import {InputTextareaModule} from "primeng/inputtextarea";
import {ImageModule} from "primeng/image";
import {TooltipModule} from "primeng/tooltip";
import {RoundPipe} from "../../pipes/round.pipe";
import {GroupsImageSearchComponent} from "../../shared/components/groups-image-search/groups-image-search.component";
import {ALL_MAKES_SELECT, DEFAULT_SELECTED_MAKE} from "../../const";
import {
    ApproveAllMakesBtnComponent
} from "../../shared/components/approve-all-makes-btn/approve-all-makes-btn.component";
import {ImageUploadComponent} from "../../shared/components/image-upload/image-upload.component";
import {MessageService} from "primeng/api";
import {
    DeleteConfirmationDialogComponent
} from "../../shared/components/delete-confirmation-dialog/delete-confirmation-dialog.component";
import {ImageEditorComponent} from "../../shared/components/image-editor/image-editor.component";
import {
    GroupImageSjcUpdatedDialogControllerService
} from "../../data/services/group-image-sjc-updated-dialog-controller.service";
import {CustomSjcDialogComponent} from "../../shared/components/custom-sjc-dialog/custom-sjc-dialog.component";
import {BadgeModule} from "primeng/badge";
import {EditorModule} from "primeng/editor";
import {
    SafetyCostCaptionDialogComponent
} from "../../shared/components/safety-cost-caption-dialog/safety-cost-caption-dialog.component";
import {OverlayPanelModule} from "primeng/overlaypanel";

@Component({
    selector: 'app-groups',
    standalone: true,
    imports: [
        Button,
        DropdownModule,
        FormsModule,
        NgOptimizedImage,
        TableModule,
        JsonPipe,
        NgForOf,
        CardModule,
        CheckboxModule,
        DividerModule,
        ChipsModule,
        IconFieldModule,
        InputIconModule,
        InputSwitchModule,
        DialogModule,
        InputTextareaModule,
        ImageModule,
        TooltipModule,
        RoundPipe,
        GroupsImageSearchComponent,
        ApproveAllMakesBtnComponent,
        ImageUploadComponent,
        NgIf,
        DeleteConfirmationDialogComponent,
        ImageEditorComponent,
        CustomSjcDialogComponent,
        NgClass,
        BadgeModule,
        EditorModule,
        SafetyCostCaptionDialogComponent,
        OverlayPanelModule,

    ],
    templateUrl: './groups.component.html',
    styleUrl: './groups.component.css',
    encapsulation: ViewEncapsulation.None

})
export class GroupsComponent {
    @ViewChild(GroupsImageSearchComponent) groupsImageSearchComponent!: GroupsImageSearchComponent
    @ViewChild(DeleteConfirmationDialogComponent) deleteConfirmationDialogComponent!: DeleteConfirmationDialogComponent
    @ViewChild(ImageEditorComponent) imageEditor!: ImageEditorComponent;
    @ViewChild(CustomSjcDialogComponent) customSjcDialog!: CustomSjcDialogComponent;
    @ViewChild(SafetyCostCaptionDialogComponent) safetyCostCaptionDialog!: SafetyCostCaptionDialogComponent;

    groups: Make[] = [];
    selectedGroup: Make | undefined;

    defaultOptions = ['All', 'Yes', 'No']
    maxImagesOptions = [10, 25, 50, 100]

    groupImageSjcUpdateDialogController = inject(GroupImageSjcUpdatedDialogControllerService)
    groupsService = inject(GroupsService)
    makeService = inject(MakeService)
    imageService = inject(ImageService)
    messageService = inject(MessageService)

    makesLoading = true;
    imagesLoading = true;
    csvExportLoading = false;
    groupLoading = true;

    images: Item[] = []
    makes: Make[] = []
    sjcs: SJC[] = []

    selectedMake: Make | undefined = DEFAULT_SELECTED_MAKE;
    selectedShowApprove: string = 'All';
    selectedShowGroups = true;
    selectedShowGroupsWithoutApprovedImages = false;
    selectedShowGroupOnlyUsedInDna = true;
    selectedShowGroupsWithoutSafetyCostCaption = false;
    selectedShowGroupsWithoutNotes = false;
    groupName = ''
    selectedMaxImages = 50;
    totalRecords = 53;
    perPage = 10
    page = 1;

    dialogCaption: string | undefined | null;

    approveAllMakesDialogVisible: boolean = false;
    deleteImageDialogVisible: boolean = false;
    safetyCostCaptionDialogVisible: boolean = false;

    protected readonly ALL_MAKES_SELECT = ALL_MAKES_SELECT;
    pTableFirst = 0;
    groupImageSearch: Group | undefined

    constructor() {
        this.makeService.getMakes()
            .subscribe(value => {
                this.makes = [ALL_MAKES_SELECT, ...value];
                this.makesLoading = false
                for (const make of this.makes) {
                    if (make.id === DEFAULT_SELECTED_MAKE.id) {
                        this.selectedMake = make
                        break
                    }
                }
            })

        this.groupLoading = true;
        this.groupsService.getGroups().subscribe(value => {
            this.groups = value
            this.groupLoading = false
        })

        this.groupsService.getSjcs().subscribe(value => {
            this.sjcs = value;
        })
    }

    getParams() {
        let makeId = 0;
        if (this.selectedMake) {
            makeId = this.selectedMake.id;
        }

        if (!(this.selectedShowApprove === 'Yes')) {
            this.selectedShowApprove = ''
        }

        return {
            approved: this.selectedShowApprove,
            image_limit: this.selectedMaxImages,
            per_page: this.perPage,
            page: this.page,
            sjc_name: this.groupName,
            make_id: makeId,
            show_empty_groups: this.selectedShowGroups,
            show_groups_without_approved_images: this.selectedShowGroupsWithoutApprovedImages,
            show_groups_without_safety_cost_caption: this.selectedShowGroupsWithoutSafetyCostCaption,
            show_groups_only_used_in_dna: this.selectedShowGroupOnlyUsedInDna,
            show_groups_without_notes: this.selectedShowGroupsWithoutNotes,
        }
    }

    refresh() {
        this.imagesLoading = true;
        this.groupsService.getImages(this.getParams(), 'groups')
            .subscribe(value => {
                this.images = value.items
                this.imagesLoading = false
                this.totalRecords = value.total_pages * this.perPage
            })
    }

    loadImages($event: TableLazyLoadEvent = {}) {
        this.perPage = $event.rows || 10;
        this.page = 1;
        this.pTableFirst = 0;

        if ($event.first && $event.rows) {
            this.page = ($event.first / $event.rows) + 1
            this.pTableFirst = $event.first;
        }

        this.imagesLoading = true;
        this.groupsService.getImages(this.getParams(), 'groups')
            .subscribe(value => {
                this.images = value.items
                this.imagesLoading = false
                this.totalRecords = value.total_pages * this.perPage
            })
    }

    exportCSV() {
        this.csvExportLoading = true;
        this.groupsService.exportCSV().subscribe({
            next: (blob: Blob) => {
                this.downloadFile(blob);
                this.csvExportLoading = false;
            },
            error: () => {
                // Handle error here
                this.csvExportLoading = false;
            }
        });
    }

    private downloadFile(blob: Blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'output.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    updateImageGroup(image: ImageDetail) {
        if (!this.selectedGroup) {
            return
        }

        const params: UpdateImageGroup = {
            current_sjc_group_id: image.sjc_group_id!,
            updated_sjc_group_id: this.selectedGroup.id,
            sjc_id: image.sjc_id,
            make_id: this.selectedMake?.id!,
            image_id: image.sjc_inspection_image_id!,
            sjc_group_sjc_inspection_image_id: image.sjc_group_sjc_inspection_image_id!
        }

        this.groupsService.updateImageGroup(params).subscribe((response) => {
            this.refresh()
            this.selectedGroup = undefined
            this.messageService.add({severity: 'success', summary: 'Success', detail: 'Successfully updated group'});
        })
    }

    updateImageCaption(image: ImageDetail) {
        if (this.dialogCaption === undefined || this.dialogCaption === image.caption) {
            return
        }

        const params: UpdateImageCaption = {
            sjc_id: image.sjc_id,
            image_id: image.sjc_inspection_image_id!,
            sjc_group_id: image.sjc_group_id!,
            make_id: this.selectedMake?.id!,
            caption: this.dialogCaption,
            sjc_group_sjc_inspection_image_id: image.sjc_group_sjc_inspection_image_id!
        }

        this.groupsService.updateImageCaption(params).subscribe(() => {
            this.refresh();
            this.messageService.add({severity: 'success', summary: 'Success', detail: 'Successfully updated caption'});
        })
    }

    resetCaption(image: ImageDetail) {
        this.dialogCaption = null;
        this.updateImageCaption(image)
        this.dialogCaption = image.caption
    }

    setSelectableGroup(name: string): void {
        for (let group of this.groups) {
            if (group.name === name) {
                this.selectedGroup = group;
                break
            }
        }
    }

    onChangeCheckbox(image: ImageDetail, $event: any): void {
        this.imageService.approveGroups(image, $event.checked, this.selectedMake?.id!).subscribe((response) => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Successfully updated approved field'
            });
            this.makesLoading = true;
            this.makeService.getMakes()
                .subscribe(value => {
                    this.makes = [ALL_MAKES_SELECT, ...value];
                    this.makesLoading = false
                    for (const make of this.makes) {
                        if (make.id === this.selectedMake!.id) {
                            this.selectedMake = make
                            break
                        }
                    }
                })

        })
    }

    getGroup(group: any): Group {
        return {
            id: group[0],
            name: group[1],
            occurrence_rate: group[2],
            search_dialog_visible: false,
            upload_dialog_visible: false
        } as Group
    }

    setCurrentGroup(group: any) {
        this.groupImageSearch = this.getGroup(group)
    }

    openSearchImageGroupDialog(group: any) {
        this.groupsImageSearchComponent.reset()
        this.setCurrentGroup(group)
        this.groupsImageSearchComponent.setGroupSelectedMake(this.selectedMake!)
        this.groupsImageSearchComponent.setGroup(this.groupImageSearch!)
        this.groupsImageSearchComponent.setSearchTextValue(this.groupImageSearch?.name!)
        this.groupImageSearch!.search_dialog_visible = true
        this.groupsImageSearchComponent.search()
    }

    openUploadImageGroupDialog(group: any) {
        this.setCurrentGroup(group)
        this.groupImageSearch!.upload_dialog_visible = true
    }

    onVisibilityApproveDialogChange($event: boolean) {
        this.approveAllMakesDialogVisible = $event
    }

    onVisibilityDeleteDialogChange($event: boolean) {
        this.deleteImageDialogVisible = $event;
    }

    onVisibilitySafetyCostDialogChange($event: boolean) {
        this.safetyCostCaptionDialogVisible = $event;
    }

    approveForAllMakes(group: any, image: ImageDetail) {
        this.setCurrentGroup(group);
        this.makeService.setImageToApprove(
            image.sjc_inspection_image_id!,
            this.groupImageSearch!.id,
            image.sjc_id,
            this.selectedMake!.id
        );
        this.approveAllMakesDialogVisible = true;
    }

    onMakesClear() {
        this.selectedMake = ALL_MAKES_SELECT;
    }

    deleteImage(image: ImageDetail) {
        this.deleteImageDialogVisible = true;
        this.deleteConfirmationDialogComponent.setImage(image);
        this.deleteConfirmationDialogComponent.setSelectedMakeId(this.selectedMake?.id!);
    }

    openEditImage(image: ImageDetail, group: Group) {
        this.imageEditor.setImageType(ImageEditorImageType.GROUP_IMAGE);
        this.imageEditor.setImage(image);
        this.imageEditor.setGroup(group)
        this.imageEditor.setGroupSelectedMake(this.selectedMake!)
    }

    openCustomSjcDialog(image: ImageDetail) {
        this.groupImageSjcUpdateDialogController.setDialogVisible(true);
        this.groupImageSjcUpdateDialogController.setUpdatingImage(image);
        this.customSjcDialog.setSelectedCustomSjc(image.custom_sjc_id)
    }

    onOpenEditSafetyAndCostCaptionDialog(image: ImageDetail) {
        this.safetyCostCaptionDialogVisible = true;
        this.safetyCostCaptionDialog.setImage(image);
        this.safetyCostCaptionDialog.setSelectedMakeId(this.selectedMake!.id);
        console.log(image)
    }
}
